/**
 * Adoption / usage aggregation contract (ATRU-06; COP-134 D4).
 *
 * Counts are derived from the rows the audit hook ALREADY writes
 * (`tf_audit_event`) — no new instrumentation, no metering table. The wire
 * shape deliberately states its own horizon (`retentionDays` /
 * `truncatedByRetention`) and its own cap (`truncated`), so a caller can never
 * mistake an under-reported window for a quiet one.
 */
import { Type } from '@sinclair/typebox';
/** The dimension a summary is grouped by. */
export const UsageSummaryGroupBySchema = Type.Union([
    Type.Literal('track'),
    Type.Literal('user'),
    Type.Literal('route'),
]);
/**
 * Query for `GET /usage/summary`. `from`/`to` are ISO-8601 instants describing
 * a HALF-OPEN window `[from, to)`. Both are optional — the default is the last
 * 30 days. A window wider than 365 days is rejected (`400 invalid_input`), it
 * is never silently clamped.
 */
export const UsageSummaryQuerySchema = Type.Object({
    from: Type.Optional(Type.String({ maxLength: 64 })),
    to: Type.Optional(Type.String({ maxLength: 64 })),
    groupBy: Type.Optional(UsageSummaryGroupBySchema),
});
/**
 * One aggregated bucket. `key` is the raw dimension value (`trackId` /
 * `actorUserId` / `httpRoute`); `label` is the human name (track name, user
 * email) or the key itself when no name resolves.
 */
export const UsageSummaryRowSchema = Type.Object({
    key: Type.String(),
    label: Type.String(),
    count: Type.Integer(),
});
/**
 * `total` is the count over the WHOLE window before the row cap, so a
 * truncated list still reports an honest total. `retentionDays` is the audit
 * retention horizon in effect (0 = unlimited); `truncatedByRetention` is true
 * when `from` predates `now - retentionDays`, i.e. the HUB-064 sweeper has
 * already deleted rows the window asked for.
 */
export const UsageSummaryResponseSchema = Type.Object({
    groupBy: UsageSummaryGroupBySchema,
    from: Type.String(),
    to: Type.String(),
    retentionDays: Type.Integer(),
    truncatedByRetention: Type.Boolean(),
    total: Type.Integer(),
    rows: Type.Array(UsageSummaryRowSchema),
    truncated: Type.Boolean(),
});
//# sourceMappingURL=usage.js.map